import React from 'react'
import { Button } from './Button'
import { RichTextContent } from './RichTextContent'
import {
  TASK_PRIORITY_COLORS,
  TASK_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
} from '../../lib/config/project-config'
import { Task } from '../../lib/types/database.types'

interface UnassignedTaskReviewModalProps {
  isOpen: boolean
  tasks: Task[]
  onClose: () => void
  onApprove: (task: Task, assigneeId: string | null) => void
  onReject: (task: Task) => void
  activeTaskId?: string | null
  profileMap?: Map<string, string>
  assigneeOptions: Array<{ id: string; label: string }>
  selectedAssigneeByTaskId: Record<string, string>
  onSelectedAssigneeChange: (taskId: string, assigneeId: string) => void
  errorMessage?: string | null
}

export const UnassignedTaskReviewModal: React.FC<UnassignedTaskReviewModalProps> = ({
  isOpen,
  tasks,
  onClose,
  onApprove,
  onReject,
  activeTaskId,
  profileMap = new Map(),
  assigneeOptions,
  selectedAssigneeByTaskId,
  onSelectedAssigneeChange,
  errorMessage,
}) => {
  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-xl border border-stone-300 bg-stone-100 shadow-xl">
        <div className="flex items-center justify-between border-b border-stone-300 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">New Request Review</h2>
            <p className="text-sm text-gray-500">Review new requests and move them to approved or rejected.</p>
          </div>
          <button type="button" className="text-gray-500 hover:text-gray-700" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          {errorMessage ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{errorMessage}</div>
          ) : null}

          {tasks.length === 0 ? (
            <div className="rounded-lg border border-stone-300 bg-stone-50 p-6 text-center text-sm text-gray-600">
              No new requests remain.
            </div>
          ) : (
            tasks.map((task) => {
              const priorityColor = TASK_PRIORITY_COLORS[task.priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280'

              return (
                <div key={task.id} className="rounded-xl border border-stone-300 bg-stone-50 p-5 shadow-sm">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
                        <p className="text-sm text-gray-500">Created request awaiting triage</p>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Created By</p>
                          <p className="text-sm text-gray-800">{profileMap.get(task.created_by) || 'Unknown user'}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Requested By</p>
                          <p className="text-sm text-gray-800">{task.requested_by || 'Unspecified'}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Related App</p>
                          <p className="text-sm text-gray-800">{task.related_app}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Type</p>
                          <p className="text-sm text-gray-800">{TASK_TYPE_LABELS[task.type as keyof typeof TASK_TYPE_LABELS] || task.type}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Priority</p>
                          <p className="text-sm font-medium" style={{ color: priorityColor }}>
                            {TASK_PRIORITY_LABELS[task.priority as keyof typeof TASK_PRIORITY_LABELS] || task.priority}
                          </p>
                        </div>
                      </div>

                      <div>
                        <p className="mb-2 text-xs uppercase tracking-wide text-gray-500">Description</p>
                        <div className="rounded-lg border border-stone-200 bg-white/70 p-3 text-sm text-gray-700">
                          <RichTextContent html={task.description || ''} emptyText="No description provided." />
                        </div>
                      </div>
                    </div>

                    <div className="flex shrink-0 gap-3 lg:flex-col">
                      <div className="min-w-[220px] space-y-1">
                        <p className="text-xs uppercase tracking-wide text-gray-500">Assign To</p>
                        <select
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800"
                          value={selectedAssigneeByTaskId[task.id] || ''}
                          onChange={(event) => onSelectedAssigneeChange(task.id, event.target.value)}
                          disabled={Boolean(activeTaskId)}
                        >
                          <option value="">Unassigned</option>
                          {assigneeOptions.map((assignee) => (
                            <option key={assignee.id} value={assignee.id}>
                              {assignee.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <Button
                        type="button"
                        onClick={() => onApprove(task, selectedAssigneeByTaskId[task.id] || null)}
                        isLoading={activeTaskId === task.id}
                        disabled={Boolean(activeTaskId && activeTaskId !== task.id) || !selectedAssigneeByTaskId[task.id]}
                      >
                        Approve
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        onClick={() => onReject(task)}
                        isLoading={activeTaskId === task.id}
                        disabled={Boolean(activeTaskId && activeTaskId !== task.id)}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}